// src/pages/AdminLogin.jsx
import { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { motion } from "framer-motion";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:ital,wght@0,400;0,500;0,600;1,400&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
`;

const flicker = keyframes`
  0%, 95%, 100% { opacity: 1; }
  96% { opacity: 0.4; }
  97% { opacity: 1; }
  98% { opacity: 0.2; }
  99% { opacity: 1; }
`;

const Page = styled.div`
  min-height: 100vh;
  background: #0a0a0a;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(#1a1a1a 1px, transparent 1px),
      linear-gradient(90deg, #1a1a1a 1px, transparent 1px);
    background-size: 40px 40px;
    opacity: 0.3;
  }
`;

const Card = styled(motion.div)`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  background: #111;
  border: 1px solid #222;
  border-radius: 4px;
  padding: 48px 40px;
`;

const Logo = styled.div`
  font-family: "Bebas Neue", sans-serif;
  font-size: 13px;
  letter-spacing: 0.3em;
  color: #e8ff47;
  margin-bottom: 8px;
  animation: ${flicker} 8s infinite;
`;

const Title = styled.h1`
  font-family: "Bebas Neue", sans-serif;
  font-size: 42px;
  color: #f0f0f0;
  letter-spacing: 0.02em;
  line-height: 1;
  margin-bottom: 36px;
`;

const Field = styled.div`
  margin-bottom: 16px;

  label {
    display: block;
    font-family: "DM Sans", sans-serif;
    font-size: 11px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #555;
    margin-bottom: 8px;
  }

  input {
    width: 100%;
    background: #0d0d0d;
    border: 1px solid #222;
    border-radius: 3px;
    padding: 12px 16px;
    font-family: "DM Sans", sans-serif;
    font-size: 14px;
    color: #f0f0f0;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: #e8ff47;
    }

    &::placeholder {
      color: #333;
    }
  }
`;

const ErrorMsg = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 12px;
  color: #ff4747;
  margin-bottom: 16px;
  padding: 10px 14px;
  background: #ff474710;
  border: 1px solid #ff474730;
  border-radius: 3px;
`;

const Btn = styled(motion.button)`
  width: 100%;
  margin-top: 8px;
  padding: 14px;
  background: #e8ff47;
  border: none;
  border-radius: 3px;
  font-family: "Bebas Neue", sans-serif;
  font-size: 18px;
  letter-spacing: 0.12em;
  color: #0a0a0a;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #d4eb30;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async () => {
    if (!email || !password) return;
    setError("");
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate("/x-admin/panel");
    } catch (err) {
      setError("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  return (
    <>
      <GlobalStyle />
      <Page>
        <Card
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <Logo>⚡ Remote Control Co.</Logo>
          <Title>Admin Access</Title>

          {error && <ErrorMsg>{error}</ErrorMsg>}

          <Field>
            <label>Email</label>
            <input
              type="email"
              placeholder="admin@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </Field>

          <Field>
            <label>Password</label>
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </Field>

          <Btn
            onClick={handleLogin}
            disabled={loading}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? "Signing in..." : "Sign In"}
          </Btn>
        </Card>
      </Page>
    </>
  );
}
